/**
 * Login Form Component - SeeTex Web
 * 
 * SECURITY FEATURES:
 * - Input sanitization before submission
 * - Secure password field handling
 * - No sensitive data in error messages
 * - CSRF protection via Firebase
 * - Autocomplete attributes for password managers
 * - Keyboard trap prevention
 */

import { useState, useCallback, type FormEvent, type ChangeEvent } from "react";
import { useAuth } from "../contexts/AuthContext";
import { isValidEmail, validatePassword } from "../config/firebase";
import { FeatureShowcase } from "./FeatureShowcase";

type AuthMode = "signin" | "signup" | "reset";

export function LoginForm() {
    const {
        signIn,
        signUp,
        signInWithGoogle,
        continueAsGuest,
        resetPassword,
        loading,
        error,
        clearError,
        isDemoMode,
    } = useAuth();



    const [mode, setMode] = useState<AuthMode>("signin");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [displayName, setDisplayName] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [localError, setLocalError] = useState<string | null>(null);
    const [resetSent, setResetSent] = useState(false);

    // Password strength indicator
    const [passwordStrength, setPasswordStrength] = useState<{
        score: number;
        feedback: string;
    }>({ score: 0, feedback: "" });

    // Handle email input with sanitization
    const handleEmailChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        setEmail(e.target.value);
        setLocalError(null);
        clearError();
    }, [clearError]);

    // Handle password input with strength checking
    const handlePasswordChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        const pwd = e.target.value;
        setPassword(pwd);
        setLocalError(null);
        clearError();

        // Check password strength for signup mode
        if (mode === "signup" && pwd.length > 0) {
            const validation = validatePassword(pwd);
            const score = 5 - validation.errors.length;
            setPasswordStrength({
                score,
                feedback: validation.errors[0] || "Strong password",
            });
        } else {
            setPasswordStrength({ score: 0, feedback: "" });
        }
    }, [mode, clearError]);

    // Form submission
    const handleSubmit = useCallback(
        async (e: FormEvent) => {
            e.preventDefault();
            setLocalError(null);

            // Client-side validation (defense in depth)
            if (!isValidEmail(email.trim())) {
                setLocalError("Please enter a valid email address");
                return;
            }

            if (mode === "reset") {
                await resetPassword(email);
                setResetSent(true);
                return;
            }

            if (mode === "signup") {
                const validation = validatePassword(password);
                if (!validation.valid) {
                    setLocalError(validation.errors[0]);
                    return;
                }

                if (password !== confirmPassword) {
                    setLocalError("Passwords do not match");
                    return;
                }
            }

            // Trim and sanitize before submission
            const trimmedEmail = email.trim().toLowerCase();

            if (mode === "signin") {
                await signIn(trimmedEmail, password);
            } else {
                await signUp(trimmedEmail, password, displayName.trim() || undefined);
            }
        },
        [email, password, confirmPassword, displayName, mode, signIn, signUp, resetPassword]
    );

    // Switch between modes
    const switchMode = useCallback((newMode: AuthMode) => {
        setMode(newMode);
        setLocalError(null);
        setResetSent(false);
        clearError();
        // Clear sensitive fields when switching modes
        setPassword("");
        setConfirmPassword("");
    }, [clearError]);

    const displayError = localError || error;

    return (
        <div className="login-container">
            <FeatureShowcase />
            <div className="login-side">
            <div className="login-card">
                <div className="login-header">
                    <span className="login-logo">∫</span>
                    <h1>SeeTex</h1>
                    <p className="login-subtitle">
                        {mode === "signin" && "Sign in to continue"}
                        {mode === "signup" && "Create your account"}
                        {mode === "reset" && "Reset your password"}
                    </p>
                    {isDemoMode && (
                        <div className="login-demo-notice">
                            Demo Mode - Firebase not configured
                        </div>
                    )}
                </div>

                {resetSent ? (
                    <div className="login-success">
                        <p>
                            If an account exists with that email, you will receive
                            a password reset link shortly.
                        </p>
                        <button
                            type="button"
                            className="login-link"
                            onClick={() => switchMode("signin")}
                        >
                            Back to sign in
                        </button>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="login-form" autoComplete="on">
                        {displayError && (
                            <div className="login-error" role="alert">
                                {displayError}
                            </div>
                        )}

                        {mode === "signup" && (
                            <div className="login-field">
                                <label htmlFor="displayName">Name (optional)</label>
                                <input
                                    type="text"
                                    id="displayName"
                                    name="displayName"
                                    value={displayName}
                                    onChange={(e) => setDisplayName(e.target.value)}
                                    placeholder="Your name"
                                    autoComplete="name"
                                    maxLength={100}
                                    disabled={loading}
                                />
                            </div>
                        )}

                        <div className="login-field">
                            <label htmlFor="email">Email</label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                value={email}
                                onChange={handleEmailChange}
                                placeholder="you@example.com"
                                autoComplete="email"
                                required
                                disabled={loading}
                                aria-describedby={displayError ? "login-error" : undefined}
                            />
                        </div>

                        {mode !== "reset" && (
                            <div className="login-field">
                                <label htmlFor="password">Password</label>
                                <div className="login-password-wrapper">
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        id="password"
                                        name="password"
                                        value={password}
                                        onChange={handlePasswordChange}
                                        placeholder="••••••••"
                                        autoComplete={mode === "signin" ? "current-password" : "new-password"}
                                        required
                                        minLength={8}
                                        disabled={loading}
                                    />
                                    <button
                                        type="button"
                                        className="login-password-toggle"
                                        onClick={() => setShowPassword(!showPassword)}
                                        aria-label={showPassword ? "Hide password" : "Show password"}
                                        tabIndex={-1}
                                    >
                                        {showPassword ? (
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" /><line x1="1" y1="1" x2="23" y2="23" /></svg>
                                        ) : (
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                                        )}
                                    </button>
                                </div>
                                {mode === "signup" && password.length > 0 && (
                                    <div className={`login-password-strength strength-${passwordStrength.score}`}>
                                        <div
                                            className="strength-bar"
                                            style={{ width: `${(passwordStrength.score / 5) * 100}%` }}
                                        />
                                        <span>{passwordStrength.feedback}</span>
                                    </div>
                                )}
                            </div>
                        )}

                        {mode === "signup" && (
                            <div className="login-field">
                                <label htmlFor="confirmPassword">Confirm Password</label>
                                <input
                                    type={showPassword ? "text" : "password"}
                                    id="confirmPassword"
                                    name="confirmPassword"
                                    value={confirmPassword}
                                    onChange={(e) => {
                                        setConfirmPassword(e.target.value);
                                        setLocalError(null);
                                    }}
                                    placeholder="••••••••"
                                    autoComplete="new-password"
                                    required
                                    minLength={8}
                                    disabled={loading}
                                />
                            </div>
                        )}

                        {mode === "signup" && (
                            <p className="login-consent">
                                By creating an account, you agree to our{" "}
                                <a href="/terms.html" target="_blank" rel="noopener noreferrer">Terms of Service</a>{" "}
                                and{" "}
                                <a href="/privacy.html" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
                            </p>
                        )}

                        <button
                            type="submit"
                            className="login-submit"
                            disabled={loading}
                        >
                            {loading ? (
                                <span className="login-spinner" />
                            ) : (
                                <>
                                    {mode === "signin" && "Sign In"}
                                    {mode === "signup" && "Create Account"}
                                    {mode === "reset" && "Send Reset Link"}
                                </>
                            )}
                        </button>

                        {mode !== "reset" && (
                            <>
                                <div className="login-divider">
                                    <span>or</span>
                                </div>

                                <button
                                    type="button"
                                    className="login-google"
                                    onClick={signInWithGoogle}
                                    disabled={loading}
                                >
                                    <svg viewBox="0 0 24 24" width="18" height="18">
                                        <path
                                            fill="#4285F4"
                                            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                                        />
                                        <path
                                            fill="#34A853"
                                            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                                        />
                                        <path
                                            fill="#FBBC05"
                                            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                                        />
                                        <path
                                            fill="#EA4335"
                                            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                                        />
                                    </svg>
                                    Continue with Google
                                </button>

                                <button
                                    type="button"
                                    className="login-guest"
                                    onClick={continueAsGuest}
                                    disabled={loading}
                                >
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                                        <circle cx="12" cy="7" r="4" />
                                    </svg>
                                    Continue as Guest
                                </button>
                            </>
                        )}
                    </form>
                )}

                <div className="login-footer">
                    {mode === "signin" && (
                        <>
                            <button
                                type="button"
                                className="login-link"
                                onClick={() => switchMode("reset")}
                            >
                                Forgot password?
                            </button>
                            <span className="login-separator">•</span>
                            <button
                                type="button"
                                className="login-link"
                                onClick={() => switchMode("signup")}
                            >
                                Create account
                            </button>
                        </>
                    )}
                    {mode === "signup" && (
                        <button
                            type="button"
                            className="login-link"
                            onClick={() => switchMode("signin")}
                        >
                            Already have an account? Sign in
                        </button>
                    )}
                    {mode === "reset" && (
                        <button
                            type="button"
                            className="login-link"
                            onClick={() => switchMode("signin")}
                        >
                            Back to sign in
                        </button>
                    )}
                </div>
                <div className="login-legal">
                    <a href="/terms.html" target="_blank" rel="noopener noreferrer">Terms of Service</a>
                    <span className="login-separator">•</span>
                    <a href="/privacy.html" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                </div>

            </div>
            </div>
        </div>
    );
}
